"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { FetchDropdown } from "@/fetch-ui/FetchDropdown";
import { FetchTextArea } from "@/fetch-ui/FetchTextArea";
import { useToast } from "@/components/providers/FetchToastProvider";
import { venuePartner } from "@/lib/database/client/database";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { useEffect, useState } from "react";
import {
  EMPTY_PARTNER_FORM,
  PartnerFormErrors,
  PartnerFormValues,
  toPartnerSignup,
  validatePartnerForm,
  visiblePartnerFields,
} from "../_logic/partner-form";
import { PARTNER_TYPE_COPY, PARTNER_TYPE_OPTIONS, PartnerType } from "@/lib/constants/venue-partner-types";
import PartnerFieldLabel from "./PartnerFieldLabel";
import PartnerTextField, { PartnerTextFieldConfig } from "./PartnerTextField";
import PartnerThankYou from "./PartnerThankYou";

/** Fields above the partner type dropdown, in order. Their wording never changes. */
const ABOUT_YOU_FIELDS: PartnerTextFieldConfig[] = [
  { key: "fullName", label: "Your name", placeholder: "Jane Smith", type: "text", autoComplete: "name" },
  { key: "email", label: "Your email", placeholder: "jane@hotel.com", type: "email", autoComplete: "email" },
  {
    key: "role",
    label: "Your role",
    placeholder: "Director of sales",
    type: "text",
    autoComplete: "organization-title",
  },
];

const OTHER_FIELD: PartnerTextFieldConfig = {
  key: "partnerTypeOther",
  label: "Please specify",
  placeholder: "Event venue marketplace",
  type: "text",
  autoComplete: "off",
};

/**
 * Builds the name and website fields, worded for the picked partner type.
 *
 * @param partnerType - The picked answer to "What best describes you?".
 * @returns The name field, then the website field.
 */
function partnerFields(partnerType: PartnerType): PartnerTextFieldConfig[] {
  const { noun, namePlaceholder, websitePlaceholder } = PARTNER_TYPE_COPY[partnerType];
  return [
    {
      key: "propertyName",
      label: `${noun} name`,
      placeholder: namePlaceholder,
      type: "text",
      autoComplete: "organization",
    },
    {
      key: "propertyWebsite",
      label: `${noun} website`,
      placeholder: websitePlaceholder,
      type: "url",
      autoComplete: "url",
    },
  ];
}

/**
 * Partner sign-up form on the /partners page. Saves to `nowadays.venue_partners`
 * and swaps to a thank-you state on success.
 *
 * @returns The sign-up form, or the thank-you state after a successful submit.
 */
export default function PartnerForm() {
  // Hooks
  const { showToast } = useToast();

  // States
  const [supabase] = useState(() => createClient());
  const [values, setValues] = useState<PartnerFormValues>(EMPTY_PARTNER_FORM);
  // Fields that were on screen at the last submit. Only these show errors, so a field that
  // appears after a submit, e.g. on picking a type, stays clean until the next submit.
  const [checkedFields, setCheckedFields] = useState<Array<keyof PartnerFormValues>>([]);
  const [submitting, setSubmitting] = useState(false);
  // Word for what was just submitted, e.g. "Company". Null until a submit succeeds.
  const [submittedNoun, setSubmittedNoun] = useState<string | null>(null);

  // Derived values
  const visibleFields = visiblePartnerFields(values);
  const allErrors = validatePartnerForm(values);
  const errors: PartnerFormErrors = Object.fromEntries(checkedFields.map((key) => [key, allErrors[key]]));

  // Handlers
  const handleChange = (key: keyof PartnerFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCheckedFields(visibleFields);
    if (Object.keys(allErrors).length > 0) return;

    setSubmitting(true);
    try {
      const signup = toPartnerSignup(values);
      await venuePartner(supabase, signup);
      setValues(EMPTY_PARTNER_FORM);
      setCheckedFields([]);
      setSubmittedNoun(PARTNER_TYPE_COPY[signup.partnerType].noun);
    } catch (err) {
      console.error("PartnerForm.handleSubmit: sign-up failed", err);
      showToast("Submission failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Effects
  // Prefill name and email for signed-in users. Never overwrite what the venue already typed.
  useEffect(() => {
    (async () => {
      const user = await getCurrentUser(supabase);
      if (!user) return;
      setValues((prev) => ({
        ...prev,
        fullName: prev.fullName || `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
        email: prev.email || user.email || "",
      }));
    })();
  }, [supabase]);

  // Render
  if (submittedNoun) return <PartnerThankYou noun={submittedNoun} onSubmitAnother={() => setSubmittedNoun(null)} />;

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
      {/* "Partner" is capitalized on purpose: for Nowadays it is a proper noun. */}
      <h2 className="text-h4 text-landing-primary">Become a Partner</h2>

      <div className="flex flex-col gap-4">
        {ABOUT_YOU_FIELDS.map((field) => (
          <PartnerTextField
            key={field.key}
            field={field}
            value={values[field.key]}
            error={errors[field.key]}
            onChange={(value) => handleChange(field.key, value)}
          />
        ))}

        <div className="flex flex-col gap-1.5">
          <PartnerFieldLabel htmlFor="partner-partnerType" required>
            What best describes you?
          </PartnerFieldLabel>
          <FetchDropdown
            id="partner-partnerType"
            name="partnerType"
            placeholder="Select one"
            options={PARTNER_TYPE_OPTIONS}
            value={values.partnerType}
            onChange={(e) => handleChange("partnerType", e.target.value)}
            status={errors.partnerType ? "error" : "default"}
            errorMessage={errors.partnerType}
            required
          />
        </div>

        {visibleFields.includes("partnerTypeOther") && (
          <PartnerTextField
            field={OTHER_FIELD}
            value={values.partnerTypeOther}
            error={errors.partnerTypeOther}
            onChange={(value) => handleChange("partnerTypeOther", value)}
          />
        )}

        {values.partnerType &&
          partnerFields(values.partnerType).map((field) => (
            <PartnerTextField
              key={field.key}
              field={field}
              value={values[field.key]}
              error={errors[field.key]}
              onChange={(value) => handleChange(field.key, value)}
            />
          ))}

        <div className="flex flex-col gap-1.5">
          <PartnerFieldLabel htmlFor="partner-referralSource">How did you hear about us?</PartnerFieldLabel>
          <FetchTextArea
            id="partner-referralSource"
            name="referralSource"
            placeholder="A colleague, a Nowadays RFP, LinkedIn..."
            rows={3}
            value={values.referralSource}
            onChange={(e) => handleChange("referralSource", e.target.value)}
          />
        </div>
      </div>

      <FetchButton type="submit" variant="primary" color="primary" size="md" loading={submitting} className="w-full">
        Submit
      </FetchButton>
    </form>
  );
}
